import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ConsumableRecord } from '../types/records'
import { matchesListSearch } from '../lib/listSearch'
import { unitLabel } from '../lib/units'
import {
  cn,
  pickerFieldClass,
  pickerListClass,
  pickerOptionClass,
  pickerPanelClass,
} from '../lib/ui'

type PanelCoords = { top: number; left: number; width: number; maxHeight: number }

type ConsumablePickerProps = {
  consumables: ConsumableRecord[]
  value: string
  onChange: (consumableId: string) => void
  /** Подпись для скринридера (например, номер строки комплектации) */
  ariaLabel?: string
}

export function ConsumablePicker({ consumables, value, onChange, ariaLabel }: ConsumablePickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [panelCoords, setPanelCoords] = useState<PanelCoords | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  /** Актуальный id выбора для колбэков после blur/rAF — иначе замыкание видит старый value до коммита родителя */
  const valueRef = useRef(value)
  valueRef.current = value
  const listId = useRef(`consumable-picker-list-${Math.random().toString(36).slice(2, 9)}`).current

  const selected = useMemo(
    () => (value ? consumables.find((c) => c.id === value) : undefined),
    [consumables, value],
  )

  const filtered = useMemo(
    () =>
      consumables.filter((c) =>
        matchesListSearch(
          query,
          c.name,
          c.code,
          c.expand?.consumable_category?.name,
          c.notes,
        ),
      ),
    [consumables, query],
  )

  /** Подставить название выбранного расходника, когда поле не в фокусе */
  useEffect(() => {
    if (document.activeElement === inputRef.current) return
    setQuery(selected ? selected.name : '')
  }, [selected?.id, selected?.name])

  const updatePanelCoords = useCallback(() => {
    const el = inputRef.current
    if (!open || !el) return
    const rect = el.getBoundingClientRect()
    const pad = 8
    const minW = 260
    const width = Math.min(Math.max(rect.width, minW), window.innerWidth - pad * 2)
    let left = rect.left
    if (left + width > window.innerWidth - pad) left = window.innerWidth - width - pad
    if (left < pad) left = pad
    const top = rect.bottom + 4
    const capPx = Math.min(window.innerHeight * 0.7, 22 * 16)
    const maxHeight = Math.max(140, Math.min(capPx, window.innerHeight - top - pad))
    setPanelCoords({ top, left, width, maxHeight })
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      setPanelCoords(null)
      return
    }
    updatePanelCoords()
    window.addEventListener('resize', updatePanelCoords)
    window.addEventListener('scroll', updatePanelCoords, true)
    return () => {
      window.removeEventListener('resize', updatePanelCoords)
      window.removeEventListener('scroll', updatePanelCoords, true)
    }
  }, [open, updatePanelCoords])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function pick(id: string) {
    const name = consumables.find((c) => c.id === id)?.name ?? ''
    valueRef.current = id
    onChange(id)
    setQuery(name)
    setOpen(false)
    inputRef.current?.blur()
  }

  function clearSelection() {
    onChange('')
    setQuery('')
    setOpen(true)
    inputRef.current?.focus()
  }

  function handleInputBlur() {
    requestAnimationFrame(() => {
      const a = document.activeElement
      if (panelRef.current?.contains(a) || wrapRef.current?.contains(a)) return
      setOpen(false)
      const v = valueRef.current
      const s = v ? consumables.find((c) => c.id === v) : undefined
      setQuery(s ? s.name : '')
    })
  }

  const panel =
    open &&
    panelCoords &&
    createPortal(
      <div
        ref={panelRef}
        id={listId}
        className={pickerPanelClass}
        role="listbox"
        aria-label="Список расходников"
        style={{
          top: panelCoords.top,
          left: panelCoords.left,
          width: panelCoords.width,
          maxHeight: panelCoords.maxHeight,
        }}
      >
        <ul className={pickerListClass}>
          {filtered.slice(0, 250).map((c) => {
            const meta = [c.code?.trim(), unitLabel(c.unit), c.expand?.consumable_category?.name]
              .filter(Boolean)
              .join(' · ')
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.id === value}
                  className={pickerOptionClass(c.id === value)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(c.id)}
                >
                  <span className="text-[0.9rem] font-medium leading-snug">{c.name}</span>
                  {meta ? <span className="text-xs text-slate-500">{meta}</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
        {filtered.length === 0 ? (
          <p className="m-0 px-4 py-3 text-center text-[0.88rem] text-slate-400">Ничего не найдено</p>
        ) : null}
        {value ? (
          <div className="border-t border-slate-100 bg-slate-50 px-2 py-1.5">
            <button
              type="button"
              className="cursor-pointer rounded-md border-none bg-transparent px-2 py-1 text-[0.82rem] text-slate-500 hover:bg-red-50 hover:text-red-700"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearSelection}
            >
              Сбросить выбор
            </button>
          </div>
        ) : null}
      </div>,
      document.body,
    )

  return (
    <div className="relative w-full" ref={wrapRef}>
      <div className={pickerFieldClass(open)}>
        <input
          ref={inputRef}
          type="text"
          className="min-w-0 flex-1 rounded-[10px] border-none bg-transparent px-2.5 py-2 text-[0.92rem] text-su-text-strong outline-none"
          placeholder="Поиск или выберите расходник…"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              setOpen(false)
              const s = value ? consumables.find((c) => c.id === value) : undefined
              setQuery(s ? s.name : '')
              inputRef.current?.blur()
            }
            if (e.key === 'Enter' && open && filtered.length > 0) {
              e.preventDefault()
              pick(filtered[0].id)
            }
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-r-lg border-none bg-transparent px-2 hover:bg-su-surface-muted"
          aria-hidden
          title="Список"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen((o) => !o)
            inputRef.current?.focus()
          }}
        >
          <span
            className={cn(
              'block h-0 w-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-slate-500 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </div>
      {panel}
    </div>
  )
}
